import { Experiment, ExperimentClient, ExperimentUser } from '@amplitude/experiment-js-client';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import envService from './env.service';

const isNonProdEnv = ['LOCAL', 'DEV'].includes(envService.getEnv().VITE_DEPLOYMENT_TARGET);

// Feature flags that have a boolean value
export enum FeatureFlags {
  Test = 'test',
  IsRiskTrendTabEnabled = 'risk-trend-tab-enabled',
  IsRiskAssessmentUpgradeEnabled = 'risk-assessment-upgrade',
  IsLabReportUploadsEnabled = 'lab-report-uploads-enabled',
  IsWhoopEnabled = 'whoop-enabled',
}

// Feature flags that have multiple variants as a string
export enum VariantFlags {
  QuizletVersion = 'grow-720-quizlet-version',
  // Active pricing experiment. Variant *value* is the Amplitude bucket
  // label (`control` / `treatment`); displayed price comes from the
  // variant *payload* (`payload.price`). When a new price test starts,
  // bump the flag key here AND in member-backend Terraform
  // (`active_pricing_experiment_key`); keep the values in lockstep.
  PricingExperiment = 'pricing-experiment-v2',
}

const defaultFeatureFlags: Record<VariantFlags, string> = {
  [VariantFlags.QuizletVersion]: 'v6',
  [VariantFlags.PricingExperiment]: 'control',
};

export interface FeatureFlagContextValue {
  isEnabled: (name: FeatureFlags) => boolean;
  getVariant: (name: VariantFlags) => string;
  // Returns the variant's payload object (set in the Amplitude UI under
  // "Set payload type") or `undefined` if the flag has no payload / hasn't
  // resolved yet. Use this for variant-keyed data (e.g., the displayed
  // price); use `getVariant` for the bucket label (`control`/`treatment`).
  getVariantPayload: (name: VariantFlags) => unknown;
  isLoading: boolean;
  setUser: (user?: ExperimentUser) => Promise<void>;
  instance?: ExperimentClient;
}

export const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined);

export const FeatureFlagProvider: FC<PropsWithChildren> = ({ children }) => {
  const [instance, setInstance] = useState<ExperimentClient>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeFeatureFlags = async () => {
      try {
        setIsLoading(true);

        // Initialize Experiment client with Amplitude Analytics integration
        // This ensures the device ID is properly shared between Analytics and Experiment
        const experimentInstance = Experiment.initializeWithAmplitudeAnalytics(
          envService.getEnv().VITE_AMPLITUDE_API_KEY
        );

        await experimentInstance.fetch();

        setInstance(experimentInstance);

      } catch (error) {
        console.error('Failed to initialize feature flags:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeFeatureFlags();
  }, []);

  const isEnabled = (name: FeatureFlags): boolean => {
    // Check localStorage for local dev overrides (e.g., localStorage.setItem('ff_payment-page-v2-enabled', 'true'))
    const localOverride = localStorage.getItem(`ff_${name}`);
    if (localOverride === 'true') return true;
    if (localOverride === 'false') return false;

    // Default lab uploads to enabled in non-production environments
    if (isNonProdEnv && name === FeatureFlags.IsLabReportUploadsEnabled) return true;

    if (!instance || isLoading) return false;
    const variant = instance.variant(name);
    return variant.value === 'on';
  };

  const getVariant = (name: VariantFlags): string => {
    if (!instance || isLoading) return defaultFeatureFlags[name];
    const variant = instance.variant(name);
    return variant.value ?? defaultFeatureFlags[name];
  };

  const getVariantPayload = (name: VariantFlags): unknown => {
    if (!instance || isLoading) return undefined;
    return instance.variant(name).payload;
  };

  const setUser = async (user?: ExperimentUser): Promise<void> => {
    if (instance && user) {
      // When using initializeWithAmplitudeAnalytics, the device ID is automatically
      // synchronized between Analytics and Experiment SDKs
      instance.setUser(user);
      await instance.fetch(user);
    }
  };

  const contextValue: FeatureFlagContextValue = {
    isEnabled,
    getVariant,
    getVariantPayload,
    isLoading,
    setUser,
    instance,
  };

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

export default FeatureFlagProvider;
